import { lightTheme, renderProviderName } from '@vgrubs/components';

/**
 * Colors and copy shared by every place the income services are listed:
 * the Net Revenue pie, the Gross Revenue pie and the per-location "By
 * Service" drilldown.
 *
 * The colors used to be positional - `FinanceTabbedCard` handed out
 * `PIE_COLORS` by index - which meant inserting a service in the middle of a
 * list silently repainted every service after it, and a sixth entry wrapped
 * back onto vGrubs' red. They are pinned per service here instead, so a
 * service keeps its color wherever it appears and however the list grows.
 */
export const SERVICE_COLORS = {
  vGrubs: lightTheme.primary,
  vOrders: lightTheme.darkGray,
  saas: lightTheme.green,
  vDrive: lightTheme.darkBlue,
  withdraw: lightTheme.blue,
  vDriveNegativeFee: lightTheme.orange,
  serviceFee: lightTheme.darkYellow,
} as const;

/** The negative balance fee row, the same on every income list. */
export const VDRIVE_NEGATIVE_FEE_LABEL = 'vDrive Negative Balance Fee';
export const VDRIVE_NEGATIVE_FEE_TOOLTIP =
  'Fees merchants paid for staying in a negative vDrive balance, counted on the day they were paid.';

/** The partner API service fee row, the same on every income list. */
export const SERVICE_FEE_LABEL = 'Service Fee';
export const SERVICE_FEE_TOOLTIP =
  'The service fee partner API orders (Kanekt365) charge the customer: through our Stripe on card orders, from the location balance on cash pickup orders.';

/**
 * Display names for the order providers the finance breakdowns can carry.
 *
 * `renderProviderName` only knows the app's narrower `Provider` enum - the
 * list Manage Providers, menus and filters work with. The finance endpoints
 * report against the backend's full `ProviderType`, which also holds
 * marketplaces we never built screens for, and those would otherwise show up
 * as their raw database key ("cloudwaitress") in the middle of a money list.
 * Only the keys `renderProviderName` does not answer for are spelled out here.
 */
const EXTRA_PROVIDER_LABELS: Record<string, string> = {
  fleksa: 'Fleksa',
  foodhub: 'FoodHub',
  cloudwaitress: 'CloudWaitress',
  flipdish: 'Flipdish',
  caviar: 'Caviar',
  yelp: 'Yelp',
  seamless: 'Seamless',
  direct: 'Direct',
  groupon: 'Groupon',
  eat24: 'Eat24',
  tapingo: 'Tapingo',
  toast: 'Toast',
  hyperzod: 'Hyperzod',
  devourNow: 'DevourNow',
  unknown: 'Unknown',
};

/**
 * Provider key → display name, for the per-provider finance rows.
 *
 * Falls back to the raw key rather than to "Unknown": a provider the backend
 * added and we have not named yet still carries real money, and hiding which
 * one it is turns the row into a mystery. The key is at least searchable.
 */
export const financeProviderLabel = (provider: string): string =>
  renderProviderName(provider, EXTRA_PROVIDER_LABELS[provider] ?? provider) ??
  provider;
